/** @format */

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
	warrantyCallStarted,
	warrantyCallSuccessful,
	warrantyCallErrored,
	warrantySelectionDrawerOpened,
	autoSelectionIneligibleSkuResolved,
	autoSelectionPlanFound,
	autoSelectionNoPlanFound
} from './warranties.actions';
import { of, switchMap } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { selectAllProductSkus } from '../product-selection.selectors';
import { Store } from '@ngrx/store';
import { Logger } from '@shared/data-access/services';
import {
	selectIsResolvedSkuWarrantyEligible,
	selectSelectedWarranty,
	selectWarrantiesForResolvedSku,
	selectWarrantyCallState,
	selectShouldShowWarrantySelectorOnAtc
} from './warranties.selectors';
import { skuResolutionActions } from '../product-selection.actions';
import { NonMerchAvailabilityService } from '../../../../services/src/lib/non-merch/non-merch-availability.service';
import { AppSnackbarService } from '../../../../services/src/lib/app-snackbar.service';
import { selectAllKillswitches } from '../killswitches/killswitches.selectors';
// circular dep workaround
import { atcSuccessDrawerOpened } from '../../../../../add-to-cart/data-access/src/lib/+state/add-to-cart.actions';

@Injectable({ providedIn: 'root' })
export class WarrantiesEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);
	private readonly nonMerchAvailabilityService = inject(NonMerchAvailabilityService);
	private readonly logger = inject(Logger);
	private readonly snackbarService = inject(AppSnackbarService);
	private readonly snackbarOptions = {
		duration: 4000,
		showAboveDrawers: true
	};

	conditionalWarrantiesFetch$ = createEffect(() =>
		this.actions$.pipe(
			ofType(warrantySelectionDrawerOpened, atcSuccessDrawerOpened),
			concatLatestFrom(() => [
				this.store.select(selectWarrantyCallState),
				this.store.select(selectAllKillswitches),
				this.store.select(selectShouldShowWarrantySelectorOnAtc)
			]),
			filter(
				([{ type }, warrantyCallState, killswitches, shouldShowWarrantySelectorOnAtc]) => {
					if (!killswitches.warrantiesEnabled) return false;
					if (!['initial', 'error'].includes(warrantyCallState)) return false;

					if (type === atcSuccessDrawerOpened.type) {
						return shouldShowWarrantySelectorOnAtc;
					}

					return true;
				}
			),
			switchMap(() => of(warrantyCallStarted())) // warrantyCallStarted is separate because we set loading state in the reducer
		)
	);

	fetchWarranties$ = createEffect(() =>
		this.actions$.pipe(
			ofType(warrantyCallStarted),
			concatLatestFrom(() => [this.store.select(selectAllProductSkus)]),
			switchMap(([, allProductSkus]) =>
				this.nonMerchAvailabilityService.getAvailableWarranties(allProductSkus).pipe(
					map((availableWarrantiesResponse) =>
						warrantyCallSuccessful({ availableWarrantiesResponse })
					),
					catchError((error) => {
						this.logger.error('Failed to get available warranties: ', error.message);
						return of(warrantyCallErrored());
					})
				)
			)
		)
	);

	autoSetSelectedWarrantyOnSkuResolve$ = createEffect(() =>
		this.actions$.pipe(
			ofType(...skuResolutionActions),
			concatLatestFrom(() => [
				this.store.select(selectSelectedWarranty),
				this.store.select(selectWarrantiesForResolvedSku),
				this.store.select(selectIsResolvedSkuWarrantyEligible)
			]),
			filter(
				([{ resolvedSku }, previouslySelectedWarranty]) =>
					// Only fire when a warranty has been selected previously, and we are resolving a sku
					previouslySelectedWarranty !== undefined && !!resolvedSku
			),
			switchMap(
				([
					,
					previouslySelectedWarranty,
					warrantiesForResolvedSku,
					isResolvedSkuWarrantyElegible
				]) => {
					// Deselect if resolved to an ineligible sku
					if (!isResolvedSkuWarrantyElegible) {
						this.snackbarService.showSnackbar(
							'Protection plan no longer available',
							this.snackbarOptions
						);
						return of(
							autoSelectionIneligibleSkuResolved({ selectedWarranty: undefined })
						);
					}

					// Try to pick a plan of the same term length
					const warrantyToAutoSelect = warrantiesForResolvedSku.find(
						(w) => previouslySelectedWarranty?.planLength === w.planLength
					);
					if (warrantyToAutoSelect) {
						if (warrantyToAutoSelect?.planSku !== previouslySelectedWarranty?.planSku) {
							this.snackbarService.showSnackbar(
								'Protection plan selection updated',
								this.snackbarOptions
							);
						}
						return of(
							autoSelectionPlanFound({
								selectedWarranty: {
									planLength: warrantyToAutoSelect.planLength,
									planSku: warrantyToAutoSelect.planSku
								}
							})
						);
					}

					// Deselect if none found
					if (previouslySelectedWarranty !== null) {
						this.snackbarService.showSnackbar(
							'Protection plan no longer available',
							this.snackbarOptions
						);
					}
					return of(autoSelectionNoPlanFound({ selectedWarranty: undefined }));
				}
			)
		)
	);
}
